import { useEffect, useState, useMemo, useCallback } from "react";
import { EnhancedSearch } from "../components/EnhancedSearch";
import { CopyButton } from "../components/CopyButton";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { apiListBanks, apiSearchIFSC, apiCountCities } from "../utils/dataLoader";
import type { IFSCCode } from "../types/data";
import { CreditCard, Building, MapPin, Copy } from "lucide-react";
import { useSearchParams } from "react-router-dom";

export function IfscCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedBank = searchParams.get('bank') || "";
  const page = Number(searchParams.get('page')) || 1;
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const pageSize = 20;

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        next.delete('page'); // Reset pagination
        return next;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  // View mode: compact cards or table (default to cards on small screens)
  const [viewMode, setViewMode] = useState<"cards" | "table">(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'
  );

  const [apiBanks, setApiBanks] = useState<string[] | null>(null);
  const [apiBanksLoading, setApiBanksLoading] = useState(false);
  const [apiBanksError, setApiBanksError] = useState<string | null>(null);

  useEffect(() => {
    if (apiBanks) return;
    setApiBanksLoading(true);
    setApiBanksError(null);
    apiListBanks()
      .then(list => {
        // Define priority banks (case-insensitive)
        const priorityBanks = [
          'ICICI BANK', 'STATE BANK OF INDIA', "SBI India", 'HDFC BANK', 'PUNJAB NATIONAL BANK',
          'CANARA BANK', 'BANK OF BARODA', 'AXIS BANK', 'UNION BANK OF INDIA',
          'INDIAN BANK', 'BANK OF INDIA', 'CENTRAL BANK OF INDIA', 'INDIAN OVERSEAS BANK'
        ];

        const sorted = [...list].sort((a, b) => {
          const aLower = a.trim().toLowerCase();
          const bLower = b.trim().toLowerCase();
          // Check if either bank is in priority list
          const aIndex = priorityBanks.findIndex(p => p.toLowerCase() === aLower);
          const bIndex = priorityBanks.findIndex(p => p.toLowerCase() === bLower);
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          return a.localeCompare(b);
        });
        
        setApiBanks(sorted);
      })
      .catch(e => setApiBanksError(e instanceof Error ? e.message : 'Failed to load banks'))
      .finally(() => setApiBanksLoading(false));
  }, [apiBanks]);

  const banks = useMemo(() => (apiBanks ?? []) as string[], [apiBanks]);

  const suggestions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const base: string[] = [];
    base.push(...banks);
    return base.filter(v => v && v.toLowerCase().includes(term)).slice(0, 10);
  }, [searchTerm, banks, selectedBank]);

  // Data and errors
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // API-backed items and totals
  const [apiItems, setApiItems] = useState<IFSCCode[]>([]);
  const [apiTotal, setApiTotal] = useState<number | undefined>(undefined);

  // Constant IFSC total (no-query total)
  const [baseTotal, setBaseTotal] = useState<number | null>(null);
  useEffect(() => {
    // Fetch base total once (no filters)
    apiSearchIFSC({ limit: 1, offset: 0 })
      .then(({ total }) => setBaseTotal(total))
      .catch(() => setBaseTotal(null));
    apiCountCities({ limit: 1, offset: 0 })
      .then((total) => setCityTotal(total))
      .catch(() => setCityTotal(null));
  }, []);

  // Fetch results in API mode when inputs change
  useEffect(() => {
    setApiLoading(true);
    setApiError(null);
    const st = debouncedSearch.trim();
    const params: any = {
      q: st || undefined,
      bank: selectedBank || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };
    apiSearchIFSC(params)
      .then(({ items, total }) => { setApiItems(items); setApiTotal(total); })
      .catch(e => setApiError(e instanceof Error ? e.message : 'Failed to fetch IFSC data'))
      .finally(() => setApiLoading(false));
  }, [debouncedSearch, selectedBank, page]);
  const filteredIfscCodes = useMemo(() => apiItems, [apiItems]);

  // City total via API
  const [cityTotal, setCityTotal] = useState<number | null>(null);

  // Bank filters
  const bankFilters = useMemo(() => [
    { label: "All Banks", value: "", active: selectedBank === "" },
    ...banks.map((bank) => ({ label: bank, value: bank, active: selectedBank === bank })).filter((bank) => bank.value.length < "STATE BANK OF INDIA".length).slice(0, 15)
  ], [banks, selectedBank]);

  const handleFilterChange = useCallback((filterValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (filterValue) next.set('bank', filterValue);
      else next.delete('bank');
      next.delete('page'); // Reset pagination
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleBankSelection = useCallback((bank: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (bank) next.set('bank', bank);
      else next.delete('bank');
      next.delete('page'); // Reset pagination
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newPage > 1) next.set('page', newPage.toString());
      else next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian Bank IFSC Codes Database",
    "description": "Complete database of Indian bank IFSC codes for NEFT, RTGS, and IMPS transfers with branch details",
    "keywords": ["IFSC codes", "bank codes", "NEFT", "RTGS", "IMPS", "bank transfer", "Indian banks"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/ifsc-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      {(apiBanksLoading || apiLoading) && (
        <div className="text-center py-4 text-gray-500">Loading IFSC data...</div>
      )}
      {(apiError || apiBanksError) && (
        <div className="text-center py-4 text-red-600">Failed to load IFSC data. Please try again later.</div>
      )}
      <SEO
        {...routeSeo('ifsc-codes')}
        keywords="IFSC codes, bank IFSC codes, NEFT codes, RTGS codes, bank branch codes, Indian bank codes"
        structuredData={structuredData}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Bank IFSC Codes Finder
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Find Indian bank IFSC codes for NEFT, RTGS, and IMPS transfers.
            Search by bank name, branch, or location with instant copy functionality.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4" />
              <span>{baseTotal ?? (apiTotal ?? filteredIfscCodes.length)} IFSC Codes</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4" />
              <span>{banks.length} Banks</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>{cityTotal ?? Array.from(new Set(filteredIfscCodes.map(c => c.city))).length} Cities</span>
            </div>
          </div>
        </div>

        {/* Enhanced Search */}
        <EnhancedSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search IFSC codes, bank names, branches, or cities..."
          suggestions={suggestions}
          filters={bankFilters}
          onFilterChange={handleFilterChange}
          resultsCount={filteredIfscCodes.length}
          onSuggestionClick={handleBankSelection}
        />

        {/* View toggle */}
        <div className="flex items-center justify-end gap-2 mb-4">
          <span className="text-sm text-gray-500">View:</span>
          <Button size="sm" variant={viewMode === 'cards' ? 'default' : 'outline'} onClick={() => setViewMode('cards')}>Cards</Button>
          <Button size="sm" variant={viewMode === 'table' ? 'default' : 'outline'} onClick={() => setViewMode('table')}>Table</Button>
        </div>

        {(() => {
          const items = filteredIfscCodes;
          const totalPages = Math.max(1, Math.ceil((apiTotal ?? filteredIfscCodes.length) / pageSize));
          return (
            <>
              {viewMode === 'cards' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((code) => (
                    <div key={code.ifsc} className="group rounded-xl focus-within:ring-2 focus-within:ring-blue-500">
                      <Card className="hover:shadow-md group-hover:shadow-md group-active:shadow transition-all duration-300 group-active:scale-95 rounded-xl">
                        <CardHeader className="py-2">
                          <CardTitle className="flex items-center justify-between">
                            <span className="text-lg font-semibold text-green-600 group-hover:text-green-700">
                              {code.ifsc}
                            </span>
                            <div className="flex items-center space-x-2">
                              <Badge variant="secondary" className="text-[10px]">
                                {code.state}
                              </Badge>
                              <CopyButton text={code.ifsc} size="sm" />
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Bank</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium px-3 py-2 text-blue-700 max-w-[180px]" title={code.bank}>{code.bank}</span>
                                <CopyButton text={code.bank} variant="ghost" size="sm" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Branch</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium px-3 py-2 max-w-[180px]" title={code.branch}>{code.branch}</span>
                                <CopyButton text={code.branch} variant="ghost" size="sm" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">City</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium px-3 py-2 text-gray-700 truncate max-w-[180px]" title={code.city}>{code.city}</span>
                                <CopyButton text={code.city} variant="ghost" size="sm" />
                              </div>
                            </div>
                            <div>
                              <p className="text-gray-500">Address</p>
                              <p className="font-medium px-3 py-2 max-w-[180px]" title={code.address}>{code.address}</p>
                              <CopyButton text={code.address} variant="ghost" size="sm" className="w-full" />
                            </div>
                            <div className="pt-2 border-t border-gray-100">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                  const bankDetails = `${code.bank}\nBranch: ${code.branch}\nIFSC: ${code.ifsc}\nCity: ${code.city}\nAddress: ${code.address}`;
                                  navigator.clipboard.writeText(bankDetails);
                                }}
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy Bank Details
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-auto rounded-md border">
                  <table className="min-w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">IFSC</th>
                        <th className="px-3 py-2 text-left">Bank</th>
                        <th className="px-3 py-2 text-left">Branch</th>
                        <th className="px-3 py-2 text-left">City</th>
                        <th className="px-3 py-2 text-left">State</th>
                        <th className="px-3 py-2 text-left hidden md:table-cell">Address</th>
                        <th className="px-3 py-2 text-left">Copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((code) => (
                        <tr key={code.ifsc} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 focus-within:bg-gray-100 transition-colors">
                          <td className="px-3 py-2 font-semibold text-green-700">{code.ifsc}</td>
                          <td className="px-3 py-2">{code.bank}</td>
                          <td className="px-3 py-2">{code.branch}</td>
                          <td className="px-3 py-2">{code.city}</td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary" className="text-[10px]">{code.state}</Badge>
                          </td>
                          <td className="px-3 py-2 max-w-[320px] hidden md:table-cell" title={code.address}>{code.address}</td>
                          <td className="px-3 py-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bankDetails = `${code.bank}\nBranch: ${code.branch}\nIFSC: ${code.ifsc}\nCity: ${code.city}\nAddress: ${code.address}`;
                                navigator.clipboard.writeText(bankDetails);
                              }}
                            >
                              <Copy className="h-4 w-4 mr-1" /> Copy
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(Math.max(1, page - 1))}>
                    Prev
                  </Button>
                  <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </>
          );
        })()}

        {/* No Results */}
        {filteredIfscCodes.length === 0 && !apiLoading && (
          <div className="text-center py-16">
            <CreditCard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No IFSC codes found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear All Filters
            </Button>
          </div>
        )}

        {/* SEO Content Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About IFSC Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">What is an IFSC Code?</h3>
              <p className="text-sm leading-relaxed">
                IFSC (Indian Financial System Code) is an 11-digit alphanumeric code used to identify
                bank branches in India for electronic fund transfers like NEFT, RTGS, and IMPS.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">IFSC Code Format</h3>
              <p className="text-sm leading-relaxed">
                The first 4 characters represent the bank code, the 5th character is always '0'
                (reserved for future use), and the last 6 digits represent the specific branch code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}